import { useMemo, useState } from 'react';
import { Box, CircularProgress, IconButton, Link, Snackbar, Tooltip, Typography, useTheme } from '@mui/material';
import DateRangeSelector from '../../system/controls/date-range-selector';
import moment from 'moment';
import { generatePDF } from '../../dwi-functions';
import { BsPrinter, BsShare } from 'react-icons/bs';
import {
    getExecutiveGlassControlClusterSx,
    getExecutiveGlassIconButtonSx,
} from '../shared/reportSurfaceStyles';

const parseJsonValue = (value) => {
    if (!value) return null;
    if (typeof value === 'object') return value;

    try {
        return JSON.parse(value);
    } catch (error) {
        return null;
    }
};

const getReportShareSlug = (profile) => {
    if (!profile || typeof profile !== 'object') return '';

    const profileSettings = parseJsonValue(profile.settings);
    const slugCandidates = [
        profile.slug,
        profile.reportSlug,
        profile.report_slug,
        profile.clientSlug,
        profile.client_slug,
        profile.reportworksSlug,
        profile.reportwerks_slug,
        profile.reportwerksSlug,
        profileSettings?.slug,
        profileSettings?.reportSlug,
        profileSettings?.report_slug,
        profileSettings?.clientSlug,
        profileSettings?.client_slug,
    ];

    return slugCandidates.find((value) => typeof value === 'string' && value.trim().length > 0)?.trim() || '';
};

const MonthlyToolbar = (props) => {
    const theme = useTheme();
    const defaultStart = moment().subtract(7, 'days').format("YYYY-MM-DD");
    const defaultEnd = moment().format("YYYY-MM-DD");
    const [shareFeedback, setShareFeedback] = useState('');

    const selectedDateS = props.selectedDateS || sessionStorage.getItem('sDate') || defaultStart;
    const selectedDateE = props.selectedDateE || sessionStorage.getItem('eDate') || defaultEnd;
    const [clientSettings] = useState(parseJsonValue(props.reportProfile?.clientSettings) || {});
    //console.log('props', props);


    const handleRangeSelect = (rangeKey) => {
        const newStart = rangeKey?.sDate || moment(rangeKey.startDate).format('YYYY-MM-DD');
        const newEnd = rangeKey?.eDate || moment(rangeKey.endDate).format('YYYY-MM-DD');
        if (newStart !== selectedDateS || newEnd !== selectedDateE) {
            if (props.handleRangeSelect) {
                props.handleRangeSelect(rangeKey);
            } else {
                props.setSelectedDateS?.(newStart);
                props.setSelectedDateE?.(newEnd);
            }
        }
    };



    const reportTitle = props.reportTitle || props.client?.name || 'Report';
    const controlClusterSx = getExecutiveGlassControlClusterSx(theme);

    const formatDate = (value) => {
        if (!value) return '';
        const safeValue = String(value);
        const date = new Date(`${safeValue}T00:00:00Z`);
        if (Number.isNaN(date.getTime())) return safeValue;
        return new Intl.DateTimeFormat('en-US', {
            month: 'long',
            day: '2-digit',
            year: 'numeric',
            timeZone: 'UTC'
        }).format(date);
    };

    const dateRangeLabel = useMemo(() => {
        const start = formatDate(sessionStorage.getItem('sDate') || selectedDateS);
        const end = formatDate(sessionStorage.getItem('eDate') || selectedDateE);
        return start && end ? `${start} - ${end}` : `${selectedDateS} - ${selectedDateE}`;
    }, [selectedDateS, selectedDateE]);

    const handlePrint = () => {
        if (!props.reportRef?.current) {
            window.print();
            return;
        }
        generatePDF(props.reportRef, reportTitle, dateRangeLabel);
    };

    const shareSlug = useMemo(
        () => getReportShareSlug(props.reportProfile),
        [props.reportProfile]
    );

    const buildShareUrl = () => {
        if (!shareSlug) return '';

        const currentStart = sessionStorage.getItem('sDate') || selectedDateS || defaultStart;
        const currentEnd = sessionStorage.getItem('eDate') || selectedDateE || defaultEnd;
        const currentViewPath = props.currentPath || window.location.pathname || '/mr';
        const shareUrl = new URL(`/go/${encodeURIComponent(shareSlug)}`, window.location.origin);

        shareUrl.searchParams.set('s', currentStart);
        shareUrl.searchParams.set('e', currentEnd);

        if (currentViewPath.startsWith('/')) {
            shareUrl.searchParams.set('rd', currentViewPath);
        }

        return shareUrl.toString();
    };

    const handleShare = async () => {
        const shareUrl = buildShareUrl();

        if (!shareUrl) {
            setShareFeedback('Share link is unavailable for this report.');
            return;
        }

        try {
            if (!navigator?.clipboard?.writeText) {
                throw new Error('Clipboard API unavailable');
            }

            await navigator.clipboard.writeText(shareUrl);
            setShareFeedback('Share link copied to clipboard.');
        } catch (error) {
            window.prompt('Copy this share link:', shareUrl);
            setShareFeedback('Share link ready to copy.');
        }
    };


    return (
        <>
            <Box
                sx={{
                    display: 'flex',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'space-evenly',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                {(props.loading ?
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CircularProgress size={20} sx={{ display: { xs: 'none', sm: 'block' } }} />
                        <Typography sx={{ fontSize: '.5rem' }} >Refreshing...</Typography>
                    </Box>
                    : '')}
                <Typography sx={{ display: { xs: 'none', sm: 'block' } }} color="primary" variant="body1" >{(props.client ? props.client.name : '')}</Typography>

                <Box sx={{ ...controlClusterSx, display: { xs: 'flex' } }}>
                    <DateRangeSelector
                        childToParent={handleRangeSelect}
                        sDate={selectedDateS}
                        eDate={selectedDateE}
                        icon={true}
                    />
                    <Tooltip title="Copy share link">
                        <IconButton sx={getExecutiveGlassIconButtonSx(theme)} onClick={handleShare}>
                            <BsShare color={theme.palette.primary.main} size={18} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Print report">
                        <IconButton sx={getExecutiveGlassIconButtonSx(theme)} onClick={handlePrint}>
                            <BsPrinter color={theme.palette.primary.main} size={20} />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{ display: { xs: 'none', md: 'block' }, p: .5 }}>
                    <Link href={props.client.agency} target="_blank" rel="noopener noreferrer">
                        <img style={{ maxHeight: 35 }} src={`${clientSettings.reportWorksLogo}`} alt={`${props.client.clientname}`} title={`${props.client.clientname}`} />
                    </Link>
                </Box>

            </Box>
            <Snackbar
                autoHideDuration={3000}
                message={shareFeedback}
                onClose={() => setShareFeedback('')}
                open={Boolean(shareFeedback)}
            />
        </>
    );
};

MonthlyToolbar.propTypes = {

};

export default MonthlyToolbar;
